import React, { useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Spinner, Text, useToast } from '@chakra-ui/react';
import { AuthContext, KEYS } from '../context/AuthContext';
import { HttpContext } from '../context/HttpContext';
import { getMessage } from '../util';

const SsoCallback = () => {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const auth = useContext(AuthContext);
  const { authAxios, publicAxios } = useContext(HttpContext);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || '');
    const code = searchParams.get('code');
    const error = searchParams.get('error');

    // Strip the query string from the URL immediately so the code cannot leak
    // into Referer headers sent by subsequent requests.
    window.history.replaceState(null, '', window.location.pathname);

    const handleError = (errorCode) => {
      let description =
        'Single sign-on failed. Please try again or use your normal email/password login.';

      if (errorCode === 'user_not_found') {
        description =
          'SSO user not allowed. Please use normal login or contact an administrator.';
      } else if (errorCode === 'missing_email') {
        description =
          'Your SSO profile is missing an email. Please contact an administrator to update your account.';
      }

      toast({
        position: 'top-left',
        description,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });

      history.replace('/login');
    };

    const handleSuccess = async () => {
      try {
        const { data: exchangeData } = await publicAxios.post('sso/exchange', { code });

        const { token, refresh_token: refreshToken } = exchangeData;

        if (!token || !refreshToken) {
          handleError();
          return;
        }

        localStorage.setItem(KEYS.token, token);

        const { data } = await authAxios.get('users/me');

        if (data) {
          auth.setAuthState({ token, user: data, refreshToken });
        } else {
          auth.setAuthTokens({ token, refreshToken });
        }

        history.replace('/');
      } catch (onError) {
        const message = getMessage(onError);

        toast({
          position: 'top-left',
          description:
            message ||
            'Unable to complete SSO login. Please try again or use your normal login method.',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });

        history.replace('/login');
      }
    };

    if (error) {
      handleError(error);
      return;
    }

    if (code) {
      handleSuccess();
      return;
    }

    handleError();
  }, [auth, authAxios, publicAxios, history, location.search, toast]);

  return (
    <Box textAlign="center" mt="8">
      <Spinner size="lg" />
      <Text mt="4">Signing you in with Okta…</Text>
    </Box>
  );
};

export default SsoCallback;
